import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class AssignmentQuestionService {
  private questionValuesSubject = new BehaviorSubject<object>(null);
  public questionValues$ = this.questionValuesSubject.asObservable();

  constructor(private httpService: HttpService) {}

  emitQuestionValues(data: object) {
    this.questionValuesSubject.next(data);
  }

  async assignmentType() {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.AssignmentType}`),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async uploadMedia(formData, isUploadAssessment = false) {
    try {
      const queryString = isUploadAssessment ? EndPoint.AssessmentQuestionMedia : EndPoint.AssignmentQuestionMedia;
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${queryString}`, formData),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async assignmentByAssignmentAndUserId(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.AssignmentQuestion}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async assignmentQuestionsByAssignmentId(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.AssignmentQuestionByAssignmentId}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async assignmentQuestionByIds(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.assignment_questions_by_assignements_id}`,
          queryParams,
        ),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async updateAssignmentQuestion(id: number, body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.AssignmentQuestion}/${id}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async assignmentQuestionById(id: number): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.AssignmentQuestion}/${id}`),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addQuestion(body: object) {
    try {
      const repsonse = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.AssignmentQuestion}`, body),
      );
      return this.httpService.decryptToken(repsonse);
    } catch (error) {
      throw error;
    }
  }

  async deleteQuestion(id: number) {
    try {
      const response = await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.AssignmentQuestion}/${id}`),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addMultipleAssessmentQuestions(body) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.add_multiple_assessment_questions}`, body),
      );

      return this.httpService.decodeToken(token);
    } catch (error) {
      throw error;
    }
  }

  async getImageDataUrl(imageUrl: string): Promise<string> {
    if (imageUrl) {
      return fetch(imageUrl)
        .then((response) => {
          return response.blob();
        })
        .then((blob) => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        });
    }
  }

  /**
   * @description generate data for PDF
   * @param questions
   * @param generatePdfWithAnswer
   * @param isAssignment
   * @returns
   */
  async generateDataForPDF(questions, generatePdfWithAnswer: boolean, isAssignment: boolean) {
    try {
      await this.questionsImageToDataUrl(questions, isAssignment);
      const questionTable = questions.map((question: any, index) => ({
        style: 'table',
        table: {
          widths: ['*'],
          dontBreakRows: false,
          body: [
            [
              {
                text: [
                  {
                    text: `Q ${index + 1}. ${isAssignment ? question.content : question.question}\n\n`,
                    italics: true,
                    bold: true,
                    fontSize: 13,
                  },
                ],
                alignment: 'left',
                fillColor: '#d6e5ef',
                margin: [
                  5,
                  10,
                  0,
                  generatePdfWithAnswer ||
                  (isAssignment ? question.assignment_question_media_id : question.assessment_question_media_id) > 0
                    ? 0
                    : 90,
                ],
                // generatePdfWithAnswer ? 0 : 90
                border: [false, false, false, false],
              },
            ],
          ],
        },
        margin: [0, 0, 0, 15],
      }));
      if (generatePdfWithAnswer === true) {
        questionTable.forEach((value: any, index) => {
          const answerType = isAssignment ? questions[index].assignment_type_id : questions[index].type;
          const mediaAttached = isAssignment
            ? questions[index].assignment_question_media_id
            : questions[index].assessment_question_media_id;

          const mediaDetails = isAssignment
            ? questions[index].assignment_question_media
            : questions[index].assessment_question_media;

          value.table.body.push([
            {
              text: `${
                answerType === 1
                  ? generatePdfWithAnswer === true
                    ? 'MULTIPLE CHOICE\n\n'
                    : 'MULTIPLE CHOICE\n'
                  : answerType === 3
                    ? generatePdfWithAnswer === true
                      ? 'MULTIPLE CHOICE GRAPHICAL IMAGES\n\n'
                      : 'MULTIPLE CHOICE GRAPHICAL IMAGES\n'
                    : generatePdfWithAnswer === true
                      ? 'TEXT\n\n'
                      : 'TEXT\n'
              }`,
              fontSize: 11,
              margin: [5, 0, 0, 0],
              style: 'assignmentType',
              fillColor: '#d6e5ef',
              border: [false, false, false, false],
            },
          ]);
          if (mediaAttached > 0) {
            if (mediaDetails.video) {
              value.table.body.push([
                {
                  text: 'Video link :',
                  style: 'answer',
                  alignment: 'left',
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 0],
                  border: [false, false, false, false],
                },
              ]);
              value.table.body.push([
                {
                  text: mediaDetails.video.url,
                  link: mediaDetails.video.url,
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 10],
                  border: [false, false, false, false],
                },
              ]);
            } else if (mediaDetails.pdf) {
              value.table.body.push([
                {
                  text: 'pdf link :',
                  style: 'answer',
                  alignment: 'left',
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 0],
                  border: [false, false, false, false],
                },
              ]);
              value.table.body.push([
                {
                  text: mediaDetails?.pdf?.url,
                  link: mediaDetails?.pdf?.url,
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 10],
                  border: [false, false, false, false],
                },
              ]);
            } else if (mediaDetails.audio) {
              value.table.body.push([
                {
                  text: 'Audio link :',
                  style: 'answer',
                  alignment: 'left',
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 0],
                  border: [false, false, false, false],
                },
              ]);
              value.table.body.push([
                {
                  text: mediaDetails?.audio?.url,
                  link: mediaDetails?.audio?.url,
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 10],
                  border: [false, false, false, false],
                },
              ]);
            } else {
              value.table.body.push([
                {
                  image: questions[index].imgData,
                  width: 250,
                  height: 150,
                  fillColor: '#d6e5ef',
                  border: [false, false, false, false],
                  margin: [125, 0, 0, 10],
                },
              ]);
            }
          }
          value.table.body.push([
            {
              text: 'Answer :\n\n',
              style: 'answer',
              alignment: 'left',
              fillColor: '#d6e5ef',
              margin: [5, 0, 5, 0],
              border: [false, false, false, false],
            },
          ]);
          // fetch correct option image for graphical answer type
          if ((isAssignment ? questions[index].assignment_type_id : questions[index].type) === 3) {
            value.table.body.push([
              {
                image: questions[index].optionImageData,
                width: 250,
                height: 150,
                fillColor: '#d6e5ef',
                margin: [5, 0, 5, 10],
                border: [false, false, false, false],
              },
            ]);
          } else {
            value.table.body.push([
              {
                text: questions[index].answer,
                alignment: 'left',
                fillColor: '#d6e5ef',
                margin: [5, 0, 5, 10],
                border: [false, false, false, false],
              },
            ]);
          }
        });
      } else {
        questionTable.forEach((value: any, index) => {
          const mediaDetails = isAssignment
            ? questions[index].assignment_question_media
            : questions[index].assessment_question_media;
          if (mediaDetails?.id > 0) {
            if (mediaDetails.video) {
              value.table.body.push([
                {
                  text: 'Video link :',
                  style: 'answer',
                  alignment: 'left',
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 0],
                  border: [false, false, false, false],
                },
              ]);
              value.table.body.push([
                {
                  text: mediaDetails.video.url,
                  link: mediaDetails.video.url,
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 90],
                  border: [false, false, false, false],
                },
              ]);
            } else if (mediaDetails.pdf) {
              value.table.body.push([
                {
                  text: 'pdf link :',
                  style: 'answer',
                  alignment: 'left',
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 0],
                  border: [false, false, false, false],
                },
              ]);
              value.table.body.push([
                {
                  text: mediaDetails?.pdf?.url,
                  link: mediaDetails?.pdf?.url,
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 120],
                  border: [false, false, false, false],
                },
              ]);
            } else if (mediaDetails.audio) {
              value.table.body.push([
                {
                  text: 'Audio link :',
                  style: 'answer',
                  alignment: 'left',
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 0],
                  border: [false, false, false, false],
                },
              ]);
              value.table.body.push([
                {
                  text: mediaDetails?.audio?.url,
                  link: mediaDetails?.audio?.url,
                  fillColor: '#d6e5ef',
                  margin: [5, 0, 5, 120],
                  border: [false, false, false, false],
                },
              ]);
            } else {
              value.table.body.push([
                {
                  image: questions[index].imgData,
                  width: 250,
                  height: 150,
                  fillColor: '#d6e5ef',
                  border: [false, false, false, false],
                  margin: [125, 0, 0, 150],
                },
              ]);
            }
          }
        });
      }
      return questionTable;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * @description Convert question images to data url
   * @param questions
   * @param isAssignment
   */
  async questionsImageToDataUrl(questions, isAssignment: boolean) {
    try {
      for (const question of questions) {
        try {
          const media = isAssignment ? question.assignment_question_media_id : question.assessment_question_media?.id;
          const mediaDetails = isAssignment ? question.assignment_question_media : question.assessment_question_media;

          if (media > 0) {
            question['imgData'] = await this.getImageDataUrl(mediaDetails?.image?.url);
          }
          if ((isAssignment ? question?.assignment_type_id : question?.type) === 3) {
            await this.questionsOptionsToDataUrl(questions, isAssignment);
          }
        } catch (error) {
          throw error;
        }
      }
    } catch (error) {
      console.log('AssignmentQuestionService ~ questionsImageToDataUrl ~ error:', error);
    }
  }

  /**
   * @description Convert question options images to data url
   * @param questions
   * @param isAssignment
   */
  async questionsOptionsToDataUrl(questions, isAssignment: boolean) {
    try {
      for (const question of questions) {
        try {
          const answerType = isAssignment ? question?.assignment_type_id : question?.type;
          if (answerType === 3) {
            const getUrl = this.getCorrectImageUrl(question, isAssignment);
            question['optionImageData'] = await this.getImageDataUrl(getUrl);
          }
        } catch (error) {
          console.log('AssignmentQuestionService ~ questionsOptionsToDataUrl ~ error:', error);
        }
      }
    } catch (error) {
      console.log('AssignmentQuestionService ~ questionsOptionsToDataUrl ~ error:', error);
    }
  }

  /**
   * @description get correct option image url for graphical multiple choice question
   * @param question
   * @param isAssignment
   * @returns string
   */
  getCorrectImageUrl(question, isAssignment: boolean): string {
    try {
      // const options = question.options.map((o: any) => o);
      const mediaImages = isAssignment
        ? question?.assignment_option_media_images
        : question.assessment_option_media_images;
      const correctIndex = question.options.findIndex((opt: string) => opt === question.answer);

      if (correctIndex !== -1 && mediaImages?.length > 0) {
        return mediaImages[correctIndex];
      }
      return '';
    } catch (error) {
      console.log('AssignmentQuestionService ~ getCorrectImageUrl ~ error:', error);
    }
  }

  async addMultipleAssignmentQuestions(body) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.add_assignment_questions_in_bulk}`, body),
      );
      return token;
      // return this.httpService.decodeToken(token);
    } catch (error) {
      throw error;
    }
  }

  async getAssignmentDataByGradeAndSubject(body: any) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.get_assignment_data_by_subject_and_grade}`,
          body,
        ),
      );
      return response;
      // return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }
}
